/**
 * Room guide: shows a single room option with metadata and rates.
 * Uses features.roomOptions and features.flows from app container (typed via assertion below).
 */
/* eslint-disable @typescript-eslint/no-unsafe-assignment -- features from useApp('features') is any until DI types are tightened */
/* eslint-disable @typescript-eslint/no-unsafe-member-access -- features.* and roomHandle/roomOption from client model */
/* eslint-disable @typescript-eslint/no-unsafe-call -- flows.enrichRoomOption, ClientRoomOption.get */

import type { FeaturesContainer } from '@elara/client-core/containers/FeaturesContainer';
import type { RoomSelection } from '@elara/domain/models/schemas/room-schemas';

import { useEffect, useRef } from 'react';

import { extractDatePart } from '@elara/client-core/lib/date-utils';
import { combineDateAndTime } from '@elara/client-core/lib/itinerary-utils';
import { ratesHeading } from '@elara/client-core/lib/rate-order';
import {
  formatBedTypes,
  formatRoomSize,
  getBedTypeIcon,
  roomShortlistMetadata,
} from '@elara/client-core/lib/room-utils';
import { cn } from '@elara/client-core/lib/utils';
import {
  bookingDisplayStatusLabel,
  bookingStatusFlagsOf,
  resolveBookingDisplayStatus,
} from '@elara/domain/booking-status';

import { bookingStatusLabelColor } from '#lib/booking-status.js';
import { useApp, useItineraryState, useTripsState } from '#states/hooks.js';
import { Alert, AlertDescription, Loader, ShortlistButton } from '#ui';
import { Card, CardContent, CardHeader, CardTitle } from '#ui/card.js';
import { SimpleMarkdown } from '#ui/chat/SimpleMarkdown.js';
import { ElaraBenefitsList } from '#ui/ElaraBenefitsBanner.js';
import { DetailRow } from '#ui/itinerary/ItineraryDetailRow.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { PriceStatusBanner } from '#ui/PriceStatusBanner.js';

import { GUIDE_BAND_CLASSES } from './guide-band.js';
import { GuideLayout } from './GuideLayout.js';
import { GuideNotFound } from './GuideNotFound.js';

import { RatePicker, RatesSkeleton, RateStay, RefreshRatesButton } from '../RatePicker.js';

/** Stub handle when tripId is missing so we never call get() and hooks run unconditionally. */
const NO_TRIP_HANDLE = { data: undefined, error: undefined, isReady: false } as const;

export interface RoomGuideProps {
  roomOptionId: string;
}

/** The rates' heading with the rooms left and Refresh on the right, on its baseline; they wrap under it when narrow. */
const RATES_HEADER = 'flex-row flex-wrap items-baseline justify-between gap-x-6 gap-y-2 space-y-0 pb-6';
/** The rates' heading at body size, in the body face: it carries the dates now, and as a display title it outweighed the room. */
const RATES_TITLE = 'font-sans font-medium text-base @3xl:text-base';
const RATES_STAY = 'flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-muted-foreground';

export function RoomGuide({ roomOptionId }: RoomGuideProps) {
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const tripId = tripsState.current?.id;
  const { features } = useApp('features') as { features: FeaturesContainer };
  const roomOptions = features.roomOptions;
  const flows = features.flows;
  const enrichTriggeredRef = useRef(false);

  const roomHandle = tripId ? roomOptions.ClientRoomOption.get({ tripId, id: roomOptionId }) : NO_TRIP_HANDLE;
  const roomOption = roomHandle.data;
  const isEnriched = roomOption?.isEnriched === true;
  // The description is written by the room's enrichment; until that lands it is on its way.
  const isDescriptionLoading = !roomOption?.generated?.generatedAt;

  // Trigger enrichment when room option is loaded but not yet enriched (Firestore listener will re-render when done)
  useEffect(() => {
    if (
      !tripId ||
      !roomOptionId ||
      isEnriched ||
      enrichTriggeredRef.current ||
      !roomOption ||
      roomOption.rates.length === 0
    )
      return;
    enrichTriggeredRef.current = true;
    void flows.enrichRoomOption({ tripId, roomOptionId }).catch(() => {
      enrichTriggeredRef.current = false;
    });
  }, [tripId, roomOptionId, isEnriched, roomOption?.rates.length, flows, roomOption]);

  if (!tripId) {
    return <GuideNotFound guide="room" title="Room not found" />;
  }

  if (roomHandle.error) {
    return <GuideNotFound guide="room" title="Room not found" />;
  }

  if (!roomHandle.isReady || !roomOption) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader loading={true} />
      </div>
    );
  }

  tripsState.setPreviewPanelTitle(roomOption.displayName);

  const hotelId = roomOption.hotelId;
  if (!hotelId) {
    return <GuideNotFound guide="room" title="Room not found" />;
  }

  const hotel = roomOption.hotel;

  if (!hotel) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader loading={true} />
      </div>
    );
  }

  const search = roomOption.search;
  const startDateOnly = search?.startDate ? extractDatePart(search.startDate) : undefined;
  const endDateOnly = search?.endDate ? extractDatePart(search.endDate) : undefined;
  const startDateTime =
    startDateOnly && hotel.checkInTime
      ? combineDateAndTime(startDateOnly, hotel.checkInTime)
      : (startDateOnly ?? search?.startDate ?? '');
  const endDateTime =
    endDateOnly && hotel.checkOutTime
      ? combineDateAndTime(endDateOnly, hotel.checkOutTime)
      : (endDateOnly ?? search?.endDate ?? '');
  const nightsCount = roomOption.nightsCount;

  const images = roomOption.images ?? [];

  const firstBed = roomOption.bedTypes[0];
  const beds = formatBedTypes(roomOption.bedTypes);
  const elaraBenefits =
    hotel.isSerandipiansAffiliated && hotel.serandipiansBenefits && hotel.serandipiansBenefits.length > 0
      ? hotel.serandipiansBenefits
      : undefined;

  // Every booked/approved room at this option, across any rate/occupancy. Pending cancellations stay
  // listed (the booking record is still live-ish and viewable) until the cancel is confirmed (ELARA-872).
  const bookedRoomItems =
    roomOption.id != null
      ? itineraryState
          .getItineraryItemsForRoomOption(roomOption.id)
          .filter((item) => item.isBooked || item.isApproved || item.isCancellationPending)
      : [];

  return (
    <GuideLayout
      images={images}
      title={roomOption.displayName}
      // One shortlist button for the whole room: the shortlist saves the room, not a rate, and its card
      // leads with the room's lowest bookable rate for the party the room was searched for.
      actions={
        <ShortlistButton
          itemType="room"
          referenceId={roomOption.id}
          title={`${roomOption.displayName} at ${hotel.name}`}
          metadata={roomShortlistMetadata(roomOption)}
        />
      }
      metadata={
        <>
          {/* Every bed in one item ("1 King Bed, 1 Sofa Bed"), with the first bed's icon. */}
          {firstBed && beds && (
            <div className="flex items-center gap-1">
              <MaterialIcon
                icon={getBedTypeIcon(firstBed.sabreBedTypeCode ?? firstBed.code ?? 0)}
                size={IconSizes.sm}
              />
              <span>{beds}</span>
            </div>
          )}
          {roomOption.maxOccupancy && (
            <div className="flex items-center gap-1">
              <MaterialIcon icon={roomOption.maxOccupancy > 1 ? 'group' : 'person'} size={IconSizes.sm} />
              <span>Sleeps {roomOption.maxOccupancy}</span>
            </div>
          )}
          {roomOption.size && (
            <div className="flex items-center gap-1">
              <MaterialIcon icon="square_foot" size={IconSizes.sm} />
              <span>{formatRoomSize(roomOption.size)}</span>
            </div>
          )}
          {roomOption.accessibleRoom && (
            <div className="flex items-center gap-1">
              <MaterialIcon icon="accessible" size={IconSizes.sm} />
              <span>Accessible</span>
            </div>
          )}
          {roomOption.nonSmokingRoom && (
            <div className="flex items-center gap-1">
              <MaterialIcon icon="smoking_rooms" size={IconSizes.sm} />
              <span>Non-smoking</span>
            </div>
          )}
        </>
      }
    >
      {/* What the room is like, with its Elara Benefits listed above the description. */}
      {roomOption.generated?.description || isDescriptionLoading || elaraBenefits ? (
        <div className="flex flex-col gap-4 max-w-prose">
          {elaraBenefits ? <ElaraBenefitsList benefits={elaraBenefits} /> : null}

          {roomOption.generated?.description ? (
            <div className="text-pretty">
              <SimpleMarkdown text={roomOption.generated.description} />
            </div>
          ) : isDescriptionLoading ? (
            <DescriptionSkeleton />
          ) : null}
        </div>
      ) : null}
      {bookedRoomItems.length > 0 && (
        <Card className="col-span-1 col-start-1 @2xl:col-span-2 bg-transparent dark:border-0">
          <CardHeader>
            <CardTitle>Bookings</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1">
            {bookedRoomItems.map((item, index) => {
              const rate = roomOption.getRateByIdentity(item.metadata as RoomSelection);
              const rateName = rate?.generated?.name ?? rate?.name ?? `Room ${index + 1}`;
              const displayStatus = resolveBookingDisplayStatus(bookingStatusFlagsOf(item));
              const confirmation = item.booking?.confirmationNumber;
              return (
                <DetailRow
                  key={item.id}
                  icon="confirmation_number"
                  value={rateName}
                  secondary={
                    <>
                      <span className={`font-medium ${bookingStatusLabelColor(displayStatus)}`}>
                        {bookingDisplayStatusLabel(displayStatus)}
                      </span>
                      {confirmation ? ` · Confirmation #${confirmation}` : ''}
                    </>
                  }
                  onClick={() => itineraryState.openViewBookingDialog(item)}
                />
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Rates: only show when enriched; until then show loading and trigger enrichment */}
      {/* The rates, the room's availability and the rates note, together at the end of the guide. */}
      {/* On the guide's band, from just above "Choose a rate" to the panel's edges: where the room is booked. */}
      <section className={cn(GUIDE_BAND_CLASSES, 'flex flex-1 flex-col gap-8 pt-8 before:top-0')}>
        {roomOption.rates.length > 0 && !isEnriched && (
          <Card className="bg-transparent dark:border-0">
            <CardHeader className={RATES_HEADER}>
              <CardTitle className={RATES_TITLE}>{ratesHeading(startDateTime, endDateTime)}</CardTitle>
              {/* Beside the heading, on its baseline: how many of the room are left. */}
              <div className={RATES_STAY}>
                <RateStay room={roomOption} />
              </div>
            </CardHeader>
            <CardContent>
              <RatesSkeleton count={roomOption.rates.length} />
            </CardContent>
          </Card>
        )}
        {(roomOption.isAvailable || roomOption.isPartiallyAvailable) && roomOption.rates.length > 0 && isEnriched && (
          <Card className="bg-transparent dark:border-0">
            <CardHeader className={RATES_HEADER}>
              <CardTitle className={RATES_TITLE}>{ratesHeading(startDateTime, endDateTime)}</CardTitle>
              {/* Beside the heading, on its baseline: how many of the room are left, then the refresh that asks the
                  hotel again about all of them. */}
              <div className={RATES_STAY}>
                <RateStay room={roomOption} />
                <RefreshRatesButton room={roomOption} />
              </div>
            </CardHeader>
            <CardContent>
              <RatePicker room={roomOption} hotel={hotel} nightsCount={nightsCount ?? 0} />
            </CardContent>
          </Card>
        )}

        {!roomOption.isAvailable && <PriceStatusBanner type="room" model={roomOption} />}

        {/* The same width as the description and the benefits: `max-w-prose` is in characters, so it sits on a
            wrapper at the description's text size, not on the smaller-text Alert. */}
        <div className="max-w-prose">
          <Alert variant="ghost" className="flex gap-3 p-0">
            <MaterialIcon icon="info" size={IconSizes.sm} className="text-accent" />
            <AlertDescription className="text-pretty">
              Please note that all rates and availability are subject to change until confirmation. No rooms are held
              during the planning process.
            </AlertDescription>
          </Alert>
        </div>
      </section>
    </GuideLayout>
  );
}

/** The room description while the room's enrichment writes it: a few lines of prose in outline. */
function DescriptionSkeleton() {
  return (
    <div className="flex flex-col gap-2.5 py-1" role="status" aria-busy aria-label="Loading description">
      {['w-full', 'w-full', 'w-11/12', 'w-2/3'].map((width, index) => (
        <div key={index} className={`h-4 ${width} animate-pulse rounded bg-background-tertiary`} />
      ))}
    </div>
  );
}
